// Command palette (IA §2.5): Ctrl/⌘ K or "/" opens it; sections Records, Actions, Go to, Help;
// arrows move, Enter opens, Esc closes. Unavailable actions are listed with their reason rather than
// hidden. Shortcuts never fire while focus is in a text field.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import { Icon, type IconName } from "./Icon";

export type PaletteSection = "records" | "actions" | "goTo" | "help";

export interface PaletteCommand {
  id: string;
  section: PaletteSection;
  label: string;
  /** Extra words that match (synonyms, the group name). */
  keywords?: string;
  icon?: IconName;
  /** Listed but not runnable, with this reason ("Needs owner permission"). */
  unavailableReason?: string;
  run: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: PaletteCommand[];
  /** Shown under Records while record search isn't wired. */
  recordsNote?: string;
}

const ORDER: PaletteSection[] = ["records", "actions", "goTo", "help"];

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}

/** Global Ctrl/⌘ K and "/" binding. */
export function usePaletteShortcut(onOpen: () => void) {
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      } else if (
        e.key === "/" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !isTypingTarget(e.target)
      ) {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpen]);
}

export function CommandPalette({ open, onOpenChange, commands, recordsNote }: CommandPaletteProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      returnFocus.current = document.activeElement as HTMLElement | null;
      setQuery("");
      setActive(0);
      el.showModal();
      inputRef.current?.focus();
    } else if (!open && el.open) {
      el.close();
      returnFocus.current?.focus?.();
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (c: PaletteCommand) =>
      !q || `${c.label} ${c.keywords ?? ""}`.toLowerCase().includes(q);
    return ORDER.flatMap((s) => commands.filter((c) => c.section === s && match(c)));
  }, [commands, query]);

  const runAt = (i: number) => {
    const c = results[i];
    if (!c || c.unavailableReason) return;
    onOpenChange(false);
    c.run();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(results.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runAt(active);
    }
  };
  const activeId = results[active] ? `${listId}-${results[active].id}` : undefined;
  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  return (
    <dialog
      ref={ref}
      className="ady-dialog mt-[10vh] max-w-xl"
      aria-label={t("palette.label")}
      data-testid="command-palette"
      onCancel={(e) => {
        e.preventDefault();
        onOpenChange(false);
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="flex items-center gap-2 border-b border-subtle px-4">
        <Icon name="search" className="text-fg-secondary" />
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          aria-label={t("palette.placeholder")}
          placeholder={t("palette.placeholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKey}
          className="min-h-14 flex-1 bg-transparent text-lg outline-none placeholder:text-fg-tertiary"
          data-testid="command-palette-input"
        />
      </div>
      {/* Focusable so keyboard users can scroll it directly (axe scrollable-region-focusable). */}
      <div
        className="max-h-[60vh] overflow-y-auto p-2"
        tabIndex={0}
        aria-label={t("palette.label")}
      >
        <ul id={listId} role="listbox" aria-label={t("palette.label")}>
          {ORDER.map((section) => {
            const items = results.filter((r) => r.section === section);
            const showNote = section === "records" && recordsNote && !query;
            if (!items.length && !showNote) return null;
            return (
              <li key={section} role="presentation">
                <p
                  className="px-3 pb-1 pt-3 text-xs font-medium text-fg-tertiary"
                  role="presentation"
                >
                  {t(`palette.sections.${section}`)}
                </p>
                {showNote ? (
                  <p className="px-3 pb-2 text-sm text-fg-secondary">{recordsNote}</p>
                ) : null}
                <ul role="group" aria-label={t(`palette.sections.${section}`)}>
                  {items.map((c) => {
                    const i = results.indexOf(c);
                    return (
                      <li
                        key={c.id}
                        id={`${listId}-${c.id}`}
                        role="option"
                        aria-selected={i === active}
                        aria-disabled={c.unavailableReason ? true : undefined}
                        onMouseMove={() => setActive(i)}
                        onClick={() => runAt(i)}
                        className={cx(
                          "flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-3 text-md",
                          i === active ? "bg-selected" : undefined,
                          c.unavailableReason && "cursor-not-allowed text-fg-disabled",
                        )}
                        data-testid={`palette-${c.id}`}
                      >
                        <Icon
                          name={c.unavailableReason ? "lock" : (c.icon ?? "chevronRight")}
                          className="flex-none text-fg-secondary"
                        />
                        <span className="flex-1">{c.label}</span>
                        {c.unavailableReason ? (
                          <span className="text-xs text-fg-tertiary">{c.unavailableReason}</span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
        {results.length === 0 && query ? (
          <p className="px-3 py-6 text-center text-md text-fg-secondary" role="status">
            {t("palette.noResults", { query })}
          </p>
        ) : null}
      </div>
      <p className="border-t border-subtle px-4 py-2 text-xs text-fg-tertiary">
        {t("palette.hint")}
      </p>
    </dialog>
  );
}
